/**
 * «Журнал доходів і витрат» ФОП-єдинника (ч.1376, рішення власника).
 *
 * Організація з обліковою політикою «Облік лише за Журналом доходів і витрат»: надходження від
 * покупців одразу дохід, акти для закриття авансів не потрібні. Журнал показує дохід за
 * графами (отримано, повернуто, безоплатно отримане, кредиторка з минулим строком давності),
 * надходження, що не є доходом, і витрати. Автоматичні рядки — з проведених грошових
 * документів; бухгалтер дописує ручні записи і позначає надходження «не дохід».
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getMe } from '@/api/auth';
import { contractors as contractorsApi } from '@/api/catalogs';
import { monthBounds } from '@/api/finance';
import {
  EXPENSE_KIND_LABELS, INCOME_KIND_LABELS, PAYMENT_FORM_LABELS, incomeJournal,
  type Amount, type IncomeJournal, type JournalExpenseKind, type JournalIncomeKind, type JournalRow,
  type JournalSection, type PaymentForm,
} from '@/api/incomeJournal';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import SearchSelect from '@/components/SearchSelect';
import { useAllPermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useStickyState } from '@/hooks/useStickyFilters';
import BodyPortal from '@/shared/components/BodyPortal';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { MONTHS_UK } from '@/shared/components/PeriodFilter';
import { docRoute } from '@/shared/components/RelatedDocs';
import TchTable from '@/shared/components/TchTable';
import { extractApiError } from '@/utils/extractApiError';

const READ = ['documents.single_tax_calculation.manage', 'refs.fop_reports.view', 'tax.view'];
const WRITE = 'documents.single_tax_calculation.manage';

type Preset = 'month' | 'quarter' | 'year' | 'custom';
type Tab = 'income' | 'not_income' | 'expenses';

export const fmt = (v: number | string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pad2 = (n: number) => String(n).padStart(2, '0');
const dmy = (iso: string) => iso.slice(0, 10).split('-').reverse().join('.');

export function journalBounds(preset: Preset, year: number, month: number, quarter: number,
  from: string, to: string): { date_from: string; date_to: string } {
  if (preset === 'month') {
    const b = monthBounds(year, month);
    return { date_from: b.period_start, date_to: b.period_end };
  }
  if (preset === 'quarter') {
    const first = (quarter - 1) * 3 + 1;
    return { date_from: `${year}-${pad2(first)}-01`, date_to: monthBounds(year, first + 2).period_end };
  }
  if (preset === 'year') return { date_from: `${year}-01-01`, date_to: `${year}-12-31` };
  return { date_from: from, date_to: to };
}

const INCOME_TAB_KINDS = new Set(['RECEIVED', 'REFUND', 'FREE_RECEIVED', 'PAYABLE_EXPIRED']);
const NOT_INCOME_KINDS = new Set(['EXCLUDED', 'EXCLUDED_REFUND', 'NOT_INCOME']);

function kindLabel(r: JournalRow, reasons: Record<string, string>): string {
  if (r.kind === 'EXCLUDED') return `не дохід: ${reasons[r.reason ?? ''] ?? r.reason ?? ''}`;
  if (r.kind === 'EXCLUDED_REFUND') return `повернення, не дохід: ${reasons[r.reason ?? ''] ?? ''}`;
  if (r.kind === 'NOT_INCOME') return 'не дохід за рахунком';
  return (INCOME_KIND_LABELS as Record<string, string>)[r.kind]
    ?? (EXPENSE_KIND_LABELS as Record<string, string>)[r.kind] ?? r.kind;
}

function DocCell({ r }: { r: JournalRow }) {
  const route = r.document_type && r.document_id ? docRoute(r.document_type, r.document_id) : undefined;
  const text = r.document_number ? `${r.document_label} № ${r.document_number}` : r.document_label;
  return route ? <Link to={route} title={text}>{text}</Link> : <span title={text}>{text}</span>;
}

// ─── ручний запис ─────────────────────────────────────────────────────────────

interface EntryDraft {
  id: string | null; section: JournalSection; kind: JournalIncomeKind | JournalExpenseKind;
  document_date: string; payment_form: PaymentForm | null; amount: string;
  contractor_id: string; description: string;
}

function EntryModal({ orgId, draft: initial, onClose, onSaved }: {
  orgId: string; draft: EntryDraft; onClose: () => void; onSaved: () => void;
}) {
  const [d, setD] = useState<EntryDraft>(initial);
  const [err, setErr] = useState<string | null>(null);
  const contractorsQ = useQuery({ queryKey: ['contractors-all'], queryFn: () => contractorsApi.listAll() });
  const kinds = d.section === 'INCOME' ? INCOME_KIND_LABELS : EXPENSE_KIND_LABELS;
  const save = useMutation({
    mutationFn: async () => {
      const amount = d.amount.replace(',', '.').trim();
      if (!(Number(amount) > 0)) throw new Error('Вкажіть суму більше нуля');
      const body = {
        document_date: d.document_date, kind: d.kind,
        payment_form: d.section === 'INCOME' ? d.payment_form : null,
        amount, contractor_id: d.contractor_id || null, description: d.description,
      };
      if (d.id) await incomeJournal.updateEntry(d.id, body);
      else await incomeJournal.createEntry({ ...body, organization_id: orgId, section: d.section });
    },
    onSuccess: () => { setErr(null); onSaved(); },
    onError: (e) => setErr(e instanceof Error && !('isAxiosError' in e) ? e.message : extractApiError(e)),
  });
  const title = `${d.id ? 'Змінити' : 'Новий'} запис ${d.section === 'INCOME' ? 'доходу' : 'витрати'}`;
  return (
    <BodyPortal>
      <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--overlay)', display: 'flex',
        alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
        <form className="card" role="dialog" aria-label={title}
          style={{ width: 640, maxWidth: '94vw', maxHeight: '85vh', overflowY: 'auto', margin: 0 }}
          onClick={(e) => e.stopPropagation()}
          onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
          <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
            <h3 style={{ margin: 0 }}>{title}</h3>
          </div>
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label htmlFor="ij-kind">Вид</label>
              <select id="ij-kind" value={d.kind}
                onChange={(e) => setD({ ...d, kind: e.target.value as EntryDraft['kind'] })}>
                {Object.entries(kinds).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label htmlFor="ij-date">Дата</label>
              <DatePopover value={d.document_date} onChange={(v) => setD({ ...d, document_date: v })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label htmlFor="ij-amount">Сума, грн</label>
              <input id="ij-amount" inputMode="decimal" value={d.amount}
                onChange={(e) => setD({ ...d, amount: e.target.value })} />
            </div>
          </div>
          <div className="doc-grid">
            {d.section === 'INCOME' && (
              <div className="field" style={{ gridColumn: 'span 4' }}>
                <label htmlFor="ij-form">Форма</label>
                <select id="ij-form" value={d.payment_form ?? 'NONCASH'}
                  onChange={(e) => setD({ ...d, payment_form: e.target.value as PaymentForm })}>
                  {Object.entries(PAYMENT_FORM_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            )}
            <div className="field" style={{ gridColumn: d.section === 'INCOME' ? 'span 8' : 'span 12' }}>
              <label htmlFor="ij-party">Контрагент</label>
              <SearchSelect
                items={(contractorsQ.data?.items ?? []).map((c) => ({ id: c.id, label: c.name, hint: c.edrpou ?? c.inn ?? null }))}
                value={d.contractor_id} onChange={(v) => setD({ ...d, contractor_id: v })}
                placeholder="— необовʼязково —" />
            </div>
          </div>
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 12' }}>
              <label htmlFor="ij-desc">Зміст</label>
              <textarea id="ij-desc" rows={2} value={d.description}
                onChange={(e) => setD({ ...d, description: e.target.value })}
                placeholder="наприклад: виручка готівкою без касового ордера" />
            </div>
          </div>
          {err && <div className="error">{err}</div>}
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <button type="button" onClick={onClose}>скасувати</button>
            <button type="submit" className="primary" disabled={save.isPending}>зберегти</button>
          </div>
        </form>
      </div>
    </BodyPortal>
  );
}

// ─── «не дохід» ───────────────────────────────────────────────────────────────

function ExcludeModal({ orgId, row, reasons, onClose, onSaved }: {
  orgId: string; row: JournalRow; reasons: Record<string, string>; onClose: () => void; onSaved: () => void;
}) {
  const [reason, setReason] = useState(Object.keys(reasons)[0] ?? '');
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => incomeJournal.setExclusion({
      organization_id: orgId, document_type: row.document_type!, document_id: row.document_id!, reason }),
    onSuccess: () => { setErr(null); onSaved(); },
    onError: (e) => setErr(extractApiError(e)),
  });
  return (
    <BodyPortal>
      <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--overlay)', display: 'flex',
        alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
        <div className="card" role="dialog" aria-label="Не включати в дохід"
          style={{ width: 520, maxWidth: '94vw', margin: 0 }} onClick={(e) => e.stopPropagation()}>
          <h3 style={{ marginTop: 0 }}>Не включати в дохід</h3>
          <p style={{ marginTop: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            {row.document_label}{row.document_number ? ` № ${row.document_number}` : ''} від {dmy(row.date)},
            {' '}{fmt(row.amount)} грн{row.contractor_name ? ` — ${row.contractor_name}` : ''}.
            Сума зникне з доходу журналу і з бази єдиного податку.
          </p>
          <div className="field">
            <label htmlFor="ij-reason">Причина</label>
            <select id="ij-reason" value={reason} onChange={(e) => setReason(e.target.value)}>
              {Object.entries(reasons).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          {err && <div className="error">{err}</div>}
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <button type="button" onClick={onClose}>скасувати</button>
            <button type="button" className="primary" disabled={save.isPending || !reason}
              onClick={() => save.mutate()}>не включати в дохід</button>
          </div>
        </div>
      </div>
    </BodyPortal>
  );
}

// ─── підсумки за графами ──────────────────────────────────────────────────────

export function JournalTotalsTable({ j }: { j: IncomeJournal }) {
  const t = j.totals;
  const rows: [string, Amount, boolean?][] = [
    ['Отримано готівкою', t.received_cash],
    ['Отримано безготівково', t.received_noncash],
    ...(Number(t.received_in_kind) ? [['Отримано в натуральній формі', t.received_in_kind] as [string, Amount]] : []),
    ['Повернуто покупцям', t.refund],
    ['Дохід від діяльності (отримано мінус повернуто)', t.net],
    ['Безоплатно отримане', t.free_received],
    ['Кредиторська заборгованість з минулим строком давності', t.payable_expired],
    ['Разом дохід за період', t.income, true],
  ];
  const exp: [string, Amount, boolean?][] = [
    [EXPENSE_KIND_LABELS.GOODS, t.goods], [EXPENSE_KIND_LABELS.SALARY, t.salary],
    [EXPENSE_KIND_LABELS.ESV, t.esv], [EXPENSE_KIND_LABELS.OTHER, t.other],
    ['Разом витрати за період', t.expenses, true],
  ];
  const block = (title: string, list: [string, Amount, boolean?][]) => (
    <TchTable>
      <thead><tr><th style={{ textAlign: 'left' }}>{title}</th><th style={{ width: 160, textAlign: 'right' }}>Сума, грн</th></tr></thead>
      <tbody>
        {list.map(([label, v, bold]) => (
          <tr key={label} style={bold ? { fontWeight: 700 } : undefined}>
            <td>{label}</td>
            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(v)}</td>
          </tr>
        ))}
      </tbody>
    </TchTable>
  );
  return (
    <div className="doc-grid" style={{ alignItems: 'start' }}>
      <div style={{ gridColumn: 'span 7' }}>{block('Доходи', rows)}</div>
      <div style={{ gridColumn: 'span 5' }}>{block('Витрати', exp)}</div>
    </div>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'bad' }) {
  return (
    <div style={{ gridColumn: 'span 3', padding: '0.6rem 0.8rem', background: 'var(--bg-soft-2)',
      borderRadius: 'var(--radius)' }}>
      <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{label}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: '0.8rem', color: tone === 'bad' ? 'var(--badge-red-fg)' : 'var(--muted)' }}>{sub}</div>}
    </div>
  );
}

// ─── сторінка ─────────────────────────────────────────────────────────────────

export default function IncomeJournalPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { permissionsByOrg, allOrgs, loading } = useAllPermissionsByOrg();
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe });
  const orgsWithJournal = useMemo(() => allOrgs.filter((o) => o.income_journal_only
    && (permissionsByOrg[o.id] ?? []).some((p) => READ.includes(p))), [allOrgs, permissionsByOrg]);
  const today = new Date();
  const [orgId, setOrgId] = useStickyState<string>('income-journal.org', '');
  const [preset, setPreset] = useStickyState<Preset>('income-journal.preset', 'quarter');
  const [year, setYear] = useStickyState<number>('income-journal.year', today.getFullYear());
  const [month, setMonth] = useStickyState<number>('income-journal.month', today.getMonth() + 1);
  const [quarter, setQuarter] = useStickyState<number>('income-journal.quarter', Math.floor(today.getMonth() / 3) + 1);
  const [from, setFrom] = useStickyState<string>('income-journal.from', `${today.getFullYear()}-01-01`);
  const [to, setTo] = useStickyState<string>('income-journal.to', today.toISOString().slice(0, 10));
  const [tab, setTab] = useState<Tab>('income');
  const [edit, setEdit] = useState<EntryDraft | null>(null);
  const [exclude, setExclude] = useState<JournalRow | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);

  // Організація — з тих, де облік за журналом увімкнено; збережену, якої вже немає, — перша доступна.
  useEffect(() => {
    if (loading) return;
    if (!orgsWithJournal.some((o) => o.id === orgId)) setOrgId(orgsWithJournal[0]?.id ?? '');
  }, [loading, orgsWithJournal, orgId, setOrgId]);

  const bounds = useMemo(() => journalBounds(preset, year, month, quarter, from, to),
    [preset, year, month, quarter, from, to]);
  const q = useQuery({
    queryKey: ['income-journal', orgId, bounds],
    queryFn: () => incomeJournal.get({ organization_id: orgId, ...bounds }),
    enabled: !!orgId && !!bounds.date_from && !!bounds.date_to,
  });
  const j = q.data;
  const readOnlyOrg = (meQ.data?.read_only_org_ids ?? []).includes(orgId) || !!meQ.data?.read_only;
  const canWrite = !!orgId && (permissionsByOrg[orgId] ?? []).includes(WRITE) && !readOnlyOrg;
  const refresh = () => qc.invalidateQueries({ queryKey: ['income-journal'] });

  const removeEntry = useMutation({
    mutationFn: (id: string) => incomeJournal.deleteEntry(id),
    onSuccess: () => { setActionErr(null); refresh(); },
    onError: (e) => setActionErr(extractApiError(e)),
  });
  const [applyMsg, setApplyMsg] = useState<string | null>(null);
  const applyJournal = useMutation({
    mutationFn: () => incomeJournal.apply(orgId),
    onSuccess: (r) => {
      setActionErr(null);
      setApplyMsg(`Перепроведено документів: ${r.reposted}${r.stripped ? `, реалізацій і актів без доходу й боргу: ${r.stripped}` : ''}`
        + `${r.closing_document_id ? '; залишок авансів закрито бухгалтерською довідкою' : ''}.`);
      refresh();
      qc.invalidateQueries({ queryKey: ['reconciliation'] });
    },
    onError: (e) => setActionErr(extractApiError(e)),
  });
  const askApply = async () => {
    if (await confirm({
      title: 'Застосувати облік за журналом до проведених документів?',
      message: 'Надходження від покупців і повернення покупцям з дати початку буде перепроведено: '
        + 'борг покупця гаситься, решта — дохід, а не аванс. У реалізацій і актів з цієї дати '
        + 'зникнуть дохід і борг покупця (товар лишається списаним). Аванси, що лишаться, закриє '
        + 'бухгалтерська довідка. Дохід для єдиного податку не зміниться.',
      okLabel: 'застосувати',
    })) applyJournal.mutate();
  };
  const restore = useMutation({
    mutationFn: (r: JournalRow) => incomeJournal.setExclusion({
      organization_id: orgId, document_type: r.document_type!, document_id: r.document_id!, reason: null }),
    onSuccess: () => { setActionErr(null); refresh(); },
    onError: (e) => setActionErr(extractApiError(e)),
  });

  const newEntry = (section: JournalSection) => setEdit({
    id: null, section, kind: section === 'INCOME' ? 'RECEIVED' : 'OTHER',
    document_date: bounds.date_to < today.toISOString().slice(0, 10) ? bounds.date_to : today.toISOString().slice(0, 10),
    payment_form: section === 'INCOME' ? 'CASH' : null, amount: '', contractor_id: '', description: '',
  });
  const editEntry = (r: JournalRow) => setEdit({
    id: r.id, section: r.section, kind: r.kind as EntryDraft['kind'], document_date: r.date,
    payment_form: r.payment_form, amount: String(r.amount), contractor_id: r.contractor_id ?? '',
    description: r.description,
  });

  const rows = useMemo(() => (j?.rows ?? []).filter((r) => (
    tab === 'income' ? r.section === 'INCOME' && INCOME_TAB_KINDS.has(r.kind)
      : tab === 'not_income' ? NOT_INCOME_KINDS.has(r.kind)
        : r.section === 'EXPENSE')), [j, tab]);

  const actionsCell = (r: JournalRow) => {
    if (!canWrite) return null;
    if (r.source === 'MANUAL') {
      return (
        <span style={{ display: 'inline-flex', gap: 4 }}>
          <button type="button" className="btn-link" onClick={() => editEntry(r)}>змінити</button>
          <button type="button" className="btn-link" title="Видалити запис"
            onClick={async () => {
              if (await confirm({ title: 'Видалити запис журналу?', message: `${kindLabel(r, j?.reasons ?? {})}, ${fmt(r.amount)} грн` })) {
                removeEntry.mutate(r.id!);
              }
            }}>×</button>
        </span>
      );
    }
    if (r.kind === 'RECEIVED' && r.excludable) {
      return <button type="button" className="btn-link" onClick={() => setExclude(r)}
        title="Не включати це надходження в дохід (власні кошти, позика, помилково зараховані…)">не дохід…</button>;
    }
    if (r.kind === 'EXCLUDED') {
      return <button type="button" className="btn-link" onClick={() => restore.mutate(r)}>повернути в дохід</button>;
    }
    return null;
  };

  const columns: ColumnDef<JournalRow>[] = [
    { key: 'date', header: 'Дата', width: 92, cell: (r) => dmy(r.date), sortValue: (r) => r.date },
    { key: 'doc', header: 'Документ', width: 160, cell: (r) => <DocCell r={r} />,
      sortValue: (r) => `${r.document_label} ${r.document_number ?? ''}` },
    { key: 'party', header: 'Контрагент', width: 160, cell: (r) => r.contractor_name ?? '',
      sortValue: (r) => r.contractor_name ?? '' },
    { key: 'desc', header: 'Зміст', width: 210, cell: (r) => (
      <span title={[r.description, r.account ? `Рахунок: ${r.account}` : ''].filter(Boolean).join('\n')}>
        {r.description || r.account || ''}</span>) },
    { key: 'kind', header: tab === 'expenses' ? 'Вид витрати' : 'Вид', width: 150,
      cell: (r) => <span title={kindLabel(r, j?.reasons ?? {})}>{kindLabel(r, j?.reasons ?? {})}</span>,
      sortValue: (r) => r.kind },
    ...(tab === 'expenses' ? [] : [{
      key: 'form', header: 'Форма', width: 90,
      cell: (r: JournalRow) => (r.payment_form ? PAYMENT_FORM_LABELS[r.payment_form] : ''),
    }]),
    { key: 'amount', header: 'Сума, грн', width: 110, align: 'right',
      cell: (r) => (r.kind === 'REFUND' || r.kind === 'EXCLUDED_REFUND' ? `−${fmt(r.amount)}` : fmt(r.amount)),
      sortValue: (r) => Number(r.amount) },
    { key: '_actions', header: '', width: 110, cell: actionsCell },
  ];

  if (!loading && orgsWithJournal.length === 0) {
    return (
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Журнал доходів і витрат</h2>
        <p>
          Облік лише за журналом доходів і витрат не увімкнено в жодній організації, до якої у вас є доступ.
          Його вмикають у картці ФОП-єдинника: «Облікова політика» → «Облік лише за Журналом доходів і витрат».
        </p>
      </div>
    );
  }

  const limit = j?.year.limit != null ? Number(j.year.limit) : null;
  const usedPct = limit ? Math.round((Number(j?.year.income ?? 0) / limit) * 1000) / 10 : null;
  const years = [today.getFullYear() + 1, today.getFullYear(), today.getFullYear() - 1, today.getFullYear() - 2];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Журнал доходів і витрат</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <button type="button" disabled={!canWrite} onClick={() => newEntry('INCOME')}
            title={canWrite ? 'Дохід, що пройшов повз документи: готівка без ордера, безоплатно отримане, кредиторка з минулим строком давності' : 'Немає права змінювати журнал'}>
            + запис доходу
          </button>
          <button type="button" disabled={!canWrite} onClick={() => newEntry('EXPENSE')}
            title={canWrite ? 'Витрата, оплачена повз рахунок і касу організації' : 'Немає права змінювати журнал'}>
            + запис витрати
          </button>
          <button type="button" disabled={!orgId} onClick={() => navigate(
            `/accounting/income-journal/print?org=${orgId}&from=${bounds.date_from}&to=${bounds.date_to}`)}>
            друк
          </button>
        </div>
      </div>
      <p style={{ marginTop: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
        Дохід — гроші, що надійшли на рахунок і в касу від покупців, мінус повернення покупцям, плюс ваші
        ручні записи. Цей підсумок іде в декларацію і розрахунок єдиного податку. Надходження, яке не є
        доходом (власні кошти, позика, помилково зараховані), позначте «не дохід».
      </p>

      <div className="doc-grid" style={{ marginBottom: '0.5rem' }}>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label htmlFor="ij-org">Організація</label>
          <select id="ij-org" value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {orgsWithJournal.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label htmlFor="ij-preset">Період</label>
          <select id="ij-preset" value={preset} onChange={(e) => setPreset(e.target.value as Preset)}>
            <option value="month">Місяць</option>
            <option value="quarter">Квартал</option>
            <option value="year">Рік</option>
            <option value="custom">Довільний</option>
          </select>
        </div>
        {preset === 'month' && (
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label htmlFor="ij-month">Місяць</label>
            <select id="ij-month" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {MONTHS_UK.slice(1).map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
        )}
        {preset === 'quarter' && (
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label htmlFor="ij-quarter">Квартал</label>
            <select id="ij-quarter" value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
              {[1, 2, 3, 4].map((x) => <option key={x} value={x}>{x} квартал</option>)}
            </select>
          </div>
        )}
        {preset !== 'custom' ? (
          <div className="field" style={{ gridColumn: preset === 'year' ? 'span 6' : 'span 3' }}>
            <label htmlFor="ij-year">Рік</label>
            <select id="ij-year" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        ) : (
          <>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label htmlFor="ij-from">З</label>
              <DatePopover value={from} onChange={setFrom} />
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label htmlFor="ij-to">По</label>
              <DatePopover value={to} onChange={setTo} />
            </div>
          </>
        )}
      </div>

      {q.isError && <div className="error">{extractApiError(q.error)}</div>}
      {actionErr && <div className="error">{actionErr}</div>}
      {j && (
        <>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
            {j.organization.income_journal_since && <>Облік за журналом — з {dmy(j.organization.income_journal_since)}. </>}
            {j.opening_document && (
              <>Аванси покупців на цю дату закрито <Link to={`/manual-operations/${j.opening_document.id}`}>
                бухгалтерською довідкою № {j.opening_document.document_number}</Link> на {fmt(j.opening_document.amount)} грн. </>
            )}
            {j.organization.not_eligible && <span style={{ color: 'var(--badge-amber-fg)' }}>{j.organization.not_eligible}</span>}
          </div>
          {Number(j.open_advances) > 0.005 && (
            <div role="alert" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap',
              padding: '0.6rem 0.8rem', marginBottom: '0.75rem', borderRadius: 'var(--radius)',
              background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)', border: '1px solid var(--badge-amber-bd)' }}>
              <span style={{ flex: '1 1 420px' }}>
                В обліку лишились аванси покупців на {fmt(j.open_advances)} грн — їх показує монітор розрахунків як
                борг перед покупцями. Так буває, якщо документи з дати початку були проведені до того, як облік за
                журналом увімкнули.
              </span>
              <button type="button" className="primary" disabled={!canWrite || applyJournal.isPending}
                title={canWrite ? undefined : 'Немає права змінювати журнал'} onClick={() => void askApply()}>
                {applyJournal.isPending ? 'застосовуємо…' : 'застосувати до проведених документів'}
              </button>
            </div>
          )}
          {applyMsg && <div style={{ color: 'var(--badge-green-fg)', marginBottom: '0.5rem' }}>{applyMsg}</div>}
          <div className="doc-grid" style={{ marginBottom: '0.75rem' }}>
            <Kpi label="Дохід за період" value={fmt(j.totals.income)} />
            <Kpi label="З початку року" value={fmt(j.year.income)}
              sub={limit != null ? `ліміт групи ${fmt(limit)} · ${usedPct}%` : undefined}
              tone={usedPct != null && usedPct >= 90 ? 'bad' : undefined} />
            <Kpi label="Не дохід" value={fmt(Number(j.totals.excluded) + Number(j.totals.not_income))} />
            <Kpi label="Витрати" value={fmt(j.totals.expenses)} />
          </div>
        </>
      )}

      <div className="seg-tabs" role="tablist" style={{ marginBottom: '0.5rem' }}>
        {([['income', 'Доходи'], ['not_income', 'Не дохід'], ['expenses', 'Витрати']] as [Tab, string][]).map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k}
            className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>{label}</button>
        ))}
      </div>
      <DataTable
        rows={rows} columns={columns} loading={q.isLoading}
        rowKey={(r) => `${r.source}:${r.id ?? r.document_id}:${r.kind}`}
        initialSortKey="date"
        rowMuted={(r) => (NOT_INCOME_KINDS.has(r.kind) ? 0.75 : undefined)}
        emptyText={tab === 'income' ? 'За період надходжень немає.'
          : tab === 'not_income' ? 'Усі надходження за період — дохід.' : 'Витрат за період немає.'}
        storageKey={`income-journal|${tab}`}
      />
      {j && <div style={{ marginTop: '1rem' }}><JournalTotalsTable j={j} /></div>}

      {edit && orgId && <EntryModal orgId={orgId} draft={edit} onClose={() => setEdit(null)}
        onSaved={() => { setEdit(null); refresh(); }} />}
      {exclude && j && <ExcludeModal orgId={orgId} row={exclude} reasons={j.reasons}
        onClose={() => setExclude(null)} onSaved={() => { setExclude(null); refresh(); }} />}
    </div>
  );
}
